import { BaseParser } from './base';

export namespace BwsBuscador {
  export interface Detection {
    serial?: string | undefined;
    imei?: string | undefined;
    firmware?: string | undefined;
    iccid?: string | undefined;
    mac?: string | undefined;
  }
}

/**
 * Parser de leitura do BWS Buscador.
 *
 * O Buscador compartilha o protocolo do NB2 para serial/IMEI/ICCID/firmware
 * (RINS=, RIMEI=, ICCID=, RFW=) e acrescenta o MAC address BLE, gravado via
 * BLEMACW= e confirmado pelo eco MACW=.
 */
export class BwsBuscadorParser extends BaseParser {
  /**
   * Extrai o serial de uma string que contém "RINS=".
   *
   * O dispositivo devolve o serial sem os zeros à esquerda (ex: "RINS=B010007"),
   * por isso o valor é normalizado para 8 caracteres — mesmo comportamento do NB2.
   */
  static serial(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('RINS=');

    if (parts.length < 2) return undefined;

    const value = parts[1].trim().replace(/\s+/g, '');

    return value.length ? value.padStart(8, '0') : undefined;
  }

  /**
   * Extrai o IMEI de uma string que contém "RIMEI=".
   */
  static imei(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('RIMEI=');

    if (parts.length < 2) return undefined;

    const value = parts[1].trim().replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o ICCID de uma string que contém "ICCID=".
   */
  static iccid(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('ICCID=');

    if (parts.length < 2) return undefined;

    const value = parts[1].trim().replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o firmware de uma string que contém "RFW=".
   */
  static firmware(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('RFW=');

    if (parts.length < 2) return undefined;

    const value = parts[1].trim().replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o MAC address BLE do eco de confirmação do comando BLEMACW.
   *
   * Envio:    BLEMACW=4CFAC90B0000
   * Resposta: MACW=4CFAC90B0000
   *
   * O firmware não possui comando de leitura do MAC, então este eco é a única
   * confirmação disponível de que a gravação foi aceita. A linha do próprio
   * envio ("BLEMACW=...") é ignorada para não ser confundida com a resposta.
   *
   * @returns O MAC normalizado (12 hex maiúsculos) ou undefined se não casar.
   */
  static mac(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    // Ignora o eco do comando enviado — só a resposta "MACW=" confirma a gravação.
    const sanitized = input.replace(/BLEMACW=/gi, '');

    const match = sanitized.match(/MACW=\s*([0-9A-Fa-f]{12})/);
    if (!match) return undefined;

    return match[1].toUpperCase();
  }

  /**
   * Indica se o trecho já contém o eco COMPLETO do MAC (12 hex após "MACW=").
   *
   * O eco chega partido entre duas leituras da serial ("...MACW=4" e, na leitura
   * seguinte, "CFAC90B0000"). Serve como critério de parada da leitura para não
   * encerrar em cima de um valor pela metade.
   */
  static hasCompleteMac(input: string): boolean {
    return BwsBuscadorParser.mac(input) !== undefined;
  }

  static methods() {
    return {
      r_serial: BwsBuscadorParser.serial,
      r_imei: BwsBuscadorParser.imei,
      r_iccid: BwsBuscadorParser.iccid,
      r_firmware: BwsBuscadorParser.firmware,
      r_mac: BwsBuscadorParser.mac,
    };
  }
}
